import { useEffect } from 'react';
import { useFormikContext } from 'formik';
import type { SetStateAction, Dispatch } from 'react';

import { invariant } from '@e-socna/utils';
import {
  ActorTypeEnum,
  getFragmentData,
  ThirdPartyTypeEnum,
} from '@e-socna/data-layer';
import { EMPTY_OBJECT } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { Loading } from '../../../../components/Loading';
import { useThirdPartiesQuery } from '../../../../components/ThirdParties/hooks/useThirdPartiesQuery.hook';
import { ThirdPartiesCmp_ThirdParty_Fragment } from '../../../../components/ThirdParties/fragments/third-party.fragment';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFormikOnNextTick } from '../../../../hooks/useFormikOnNextTick.hook';
import type { SelectionRestriction } from '../../../../components/ThirdParties/types/selection-restriction.type';

import { ThirdPartiesSelectionFormStep } from '../../../ThirdParty/steps/Selection';

import type { ActorForm_Actor } from '../../fragments/actor.fragment';

import { useInitialGridState } from './hooks/useInitialGridState.hook';
import { useRepresentativesReset } from './hooks/useRepresentativesReset.hook';

interface Props {
  readonly excludedIds?: readonly Id[];
  readonly id: string;
  readonly isInternal: boolean;
  readonly selectionRestriction?: SelectionRestriction;
  readonly setActorType: Dispatch<SetStateAction<ActorTypeEnum>>;
  readonly setOrganizationId: Dispatch<SetStateAction<Id | undefined>>;
}

export const ActorThirdPartySelectFormStep = ({
  excludedIds,
  isInternal,
  selectionRestriction,
  setActorType,
  setOrganizationId,
}: Readonly<Props>) => {
  const { values } = useFormikContext<ActorForm_Actor>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  useRepresentativesReset();

  const { data, isSuccess } = useThirdPartiesQuery({
    isInternal,
  });

  const { actorTypesCodeMap } = useReferencesContext();

  useEffect(() => {
    if (!!values.thirdPartyId && isSuccess) {
      invariant(
        !!data && 'findSomeThirdParties' in data,
        'findSomeThirdParties must be defined',
      );

      const thirdParties = getFragmentData(
        ThirdPartiesCmp_ThirdParty_Fragment,
        data.findSomeThirdParties,
      );

      const thirdParty = thirdParties.find(
        (thirdParty) => thirdParty._id === values.thirdPartyId,
      );

      if (thirdParty) {
        const actorTypeCode =
          thirdParty.kind === ThirdPartyTypeEnum.Organization
            ? ActorTypeEnum.ActorOrganization
            : ActorTypeEnum.ActorPerson;
        const actorType = actorTypesCodeMap[actorTypeCode];
        setFieldValueOnNextTick('__typename', actorTypeCode);
        setFieldValueOnNextTick('kind', actorTypeCode);
        setFieldValueOnNextTick('typeId', actorType._id);
        setActorType(actorTypeCode);

        if (actorTypeCode === ActorTypeEnum.ActorOrganization) {
          setOrganizationId(thirdParty._id);
        }
      }
    }
  }, [
    data,
    isSuccess,
    setActorType,
    values.thirdPartyId,
    actorTypesCodeMap,
    setFieldValueOnNextTick,
    setOrganizationId,
  ]);

  const { initialGridState } = useInitialGridState();

  return (
    <>
      {!isSuccess && <Loading />}
      <ThirdPartiesSelectionFormStep
        excludedIds={excludedIds}
        fieldName="thirdPartyId"
        id="thirdPartyId"
        initialGridState={initialGridState}
        isInternal={isInternal}
        selectionRestriction={selectionRestriction}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(isInternal
          ? { kind: ThirdPartyTypeEnum.Organization }
          : EMPTY_OBJECT)}
      />
    </>
  );
};
